import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut,
  Mail,
  Phone,
  Building2,
  Trash2,
  CheckCircle2,
  Inbox,
  MessageSquare,
  RefreshCw,
  CalendarDays,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const INQUIRY_LABELS = {
  hire_talent: "Hire Talent",
  startup_hr_setup: "Startup HR Setup",
  hr_retainer: "Monthly HR Retainer",
  bulk_hiring: "Bulk Hiring",
  cv_building: "CV Building",
  interview_prep: "Interview Prep",
  career_guidance: "Career Guidance",
  other: "General",
};

const CONTACT_STATUS = ["new", "contacted", "closed"];
const BOOKING_STATUS = ["pending", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
  const [tab, setTab] = useState("contacts");
  const { email, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const handleAuthError = (err) => {
    if (err?.response?.status === 401) {
      logout();
      navigate("/admin/login", { replace: true });
      return true;
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="admin-dashboard">
      <header className="bg-white border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Career Connect" className="h-10 w-10 object-contain" />
            <span className="font-heading font-semibold">Career Connect · Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-brand-muted hidden sm:inline">{email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="rounded-full border-brand-border"
              data-testid="admin-logout"
            >
              <LogOut size={14} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl font-medium">Inbox</h1>
            <p className="text-brand-muted mt-1 text-sm">
              Inquiries and consultation bookings — all in one place.
            </p>
          </div>
          <div className="inline-flex p-1 rounded-full bg-white border border-brand-border" role="tablist">
            <button
              onClick={() => setTab("contacts")}
              data-testid="admin-tab-contacts"
              className={`px-4 h-10 rounded-full text-sm font-medium transition ${
                tab === "contacts"
                  ? "bg-brand-primary text-white shadow-sm"
                  : "text-brand-muted hover:text-brand-ink"
              }`}
            >
              Contact Leads
            </button>
            <button
              onClick={() => setTab("bookings")}
              data-testid="admin-tab-bookings"
              className={`px-4 h-10 rounded-full text-sm font-medium transition ${
                tab === "bookings"
                  ? "bg-brand-primary text-white shadow-sm"
                  : "text-brand-muted hover:text-brand-ink"
              }`}
            >
              Bookings
            </button>
          </div>
        </div>

        {tab === "contacts" ? (
          <ContactsPanel onAuthError={handleAuthError} />
        ) : (
          <BookingsPanel onAuthError={handleAuthError} />
        )}
      </main>
    </div>
  );
}

/* -------------------------- Contacts Panel -------------------------- */
function ContactsPanel({ onAuthError }) {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/admin/contacts", { params }),
        api.get("/admin/stats"),
      ]);
      setContacts(list);
      setStats(s);
    } catch (err) {
      if (onAuthError(err)) return;
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [filter, onAuthError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contacts/${id}`, { status });
      toast.success(`Marked as ${status}`);
      loadData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      toast.success("Lead deleted");
      loadData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const statusColor = (s) =>
    s === "new"
      ? "bg-brand-soft text-brand-primary"
      : s === "contacted"
      ? "bg-amber-100 text-amber-700"
      : "bg-emerald-100 text-emerald-700";

  return (
    <div className="mt-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total" value={stats.total} icon={Inbox} />
        <StatCard label="New" value={stats.new} icon={MessageSquare} accent />
        <StatCard label="Contacted" value={stats.contacted} icon={CheckCircle2} />
        <StatCard label="Closed" value={stats.closed} icon={CheckCircle2} />
      </div>

      <div className="mt-8 flex items-center gap-3 flex-wrap">
        <span className="text-sm text-brand-muted">Filter:</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger
            className="w-44 h-10 rounded-full bg-white border-brand-border"
            data-testid="admin-filter-status"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={loadData}
          className="ml-auto rounded-full border-brand-border"
          data-testid="admin-refresh"
        >
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      <div className="mt-6 space-y-4" data-testid="admin-contacts-list">
        {loading && <div className="p-10 text-center text-brand-muted">Loading leads…</div>}
        {!loading && contacts.length === 0 && (
          <div className="p-10 text-center rounded-2xl bg-white border border-brand-border text-brand-muted">
            No leads yet.
          </div>
        )}
        {!loading &&
          contacts.map((c) => (
            <div
              key={c.id}
              className="rounded-2xl bg-white border border-brand-border p-6 shadow-sm"
              data-testid={`admin-contact-${c.id}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-semibold">{c.name}</h3>
                    <Badge className={`${statusColor(c.status)} capitalize`}>{c.status}</Badge>
                    <Badge variant="secondary" className="bg-brand-surfaceAlt text-brand-ink border-brand-border">
                      {INQUIRY_LABELS[c.inquiry_type] || c.inquiry_type}
                    </Badge>
                  </div>
                  <div className="mt-2 text-xs text-brand-muted">{fmtDateTime(c.created_at)}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {CONTACT_STATUS.filter((s) => s !== c.status).map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(c.id, s)}
                      className="rounded-full border-brand-border capitalize text-xs"
                      data-testid={`admin-contact-mark-${s}`}
                    >
                      Mark {s}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => remove(c.id)}
                    className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid="admin-contact-delete"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>

              <div className="mt-4 grid md:grid-cols-3 gap-3 text-sm">
                <InfoLine icon={Mail} label={c.email} href={`mailto:${c.email}`} />
                {c.phone && <InfoLine icon={Phone} label={c.phone} href={`tel:${c.phone}`} />}
                {c.company && <InfoLine icon={Building2} label={c.company} />}
              </div>

              <p className="mt-4 text-brand-ink whitespace-pre-wrap leading-relaxed text-sm p-4 rounded-xl bg-brand-surfaceAlt">
                {c.message}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
}

/* -------------------------- Bookings Panel -------------------------- */
function BookingsPanel({ onAuthError }) {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0, cancelled: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/admin/bookings", { params }),
        api.get("/admin/bookings/stats"),
      ]);
      setBookings(list);
      setStats(s);
    } catch (err) {
      if (onAuthError(err)) return;
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [filter, onAuthError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Marked as ${status}`);
      loadData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Booking deleted");
      loadData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const statusColor = (s) =>
    ({
      pending: "bg-brand-soft text-brand-primary",
      confirmed: "bg-amber-100 text-amber-700",
      completed: "bg-emerald-100 text-emerald-700",
      cancelled: "bg-rose-100 text-rose-700",
    }[s] || "bg-gray-100 text-gray-700");

  return (
    <div className="mt-8">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Total" value={stats.total} icon={CalendarDays} />
        <StatCard label="Pending" value={stats.pending} icon={Clock} accent />
        <StatCard label="Confirmed" value={stats.confirmed} icon={CheckCircle2} />
        <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} />
        <StatCard label="Cancelled" value={stats.cancelled} icon={Trash2} />
      </div>

      <div className="mt-8 flex items-center gap-3 flex-wrap">
        <span className="text-sm text-brand-muted">Filter:</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger
            className="w-44 h-10 rounded-full bg-white border-brand-border"
            data-testid="admin-bookings-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={loadData}
          className="ml-auto rounded-full border-brand-border"
          data-testid="admin-bookings-refresh"
        >
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      <div className="mt-6 space-y-4" data-testid="admin-bookings-list">
        {loading && <div className="p-10 text-center text-brand-muted">Loading bookings…</div>}
        {!loading && bookings.length === 0 && (
          <div className="p-10 text-center rounded-2xl bg-white border border-brand-border text-brand-muted">
            No bookings yet.
          </div>
        )}
        {!loading &&
          bookings.map((b) => (
            <div
              key={b.id}
              className="rounded-2xl bg-white border border-brand-border p-6 shadow-sm"
              data-testid={`admin-booking-${b.id}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-semibold">{b.name}</h3>
                    <Badge className={`${statusColor(b.status)} capitalize`}>{b.status}</Badge>
                    <Badge variant="secondary" className="bg-brand-surfaceAlt text-brand-ink border-brand-border">
                      {INQUIRY_LABELS[b.topic] || b.topic}
                    </Badge>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-brand-primary">
                    <CalendarDays size={14} /> {b.slot_date} · {b.slot_time} IST
                  </div>
                  <div className="mt-1 text-xs text-brand-muted">
                    Booked {fmtDateTime(b.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {BOOKING_STATUS.filter((s) => s !== b.status).map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(b.id, s)}
                      className="rounded-full border-brand-border capitalize text-xs"
                      data-testid={`admin-booking-mark-${s}`}
                    >
                      Mark {s}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => remove(b.id)}
                    className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid="admin-booking-delete"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>

              <div className="mt-4 grid md:grid-cols-3 gap-3 text-sm">
                <InfoLine icon={Mail} label={b.email} href={`mailto:${b.email}`} />
                {b.phone && <InfoLine icon={Phone} label={b.phone} href={`tel:${b.phone}`} />}
              </div>

              {b.message && (
                <p className="mt-4 text-brand-ink whitespace-pre-wrap leading-relaxed text-sm p-4 rounded-xl bg-brand-surfaceAlt">
                  {b.message}
                </p>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

/* -------------------------- Shared bits -------------------------- */
function StatCard({ label, value, icon: Icon, accent = false }) {
  return (
    <div
      className={`rounded-2xl border p-5 ${
        accent
          ? "bg-brand-primary text-white border-brand-primary"
          : "bg-white border-brand-border"
      }`}
      data-testid={`admin-stat-${label.toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-xs uppercase tracking-[0.18em] ${accent ? "text-white/80" : "text-brand-muted"}`}>
          {label}
        </span>
        <Icon size={16} className={accent ? "text-white/90" : "text-brand-primary"} />
      </div>
      <div className={`mt-3 font-heading text-3xl font-medium ${accent ? "text-white" : ""}`}>{value}</div>
    </div>
  );
}

function InfoLine({ icon: Icon, label, href }) {
  const content = (
    <span className="inline-flex items-center gap-2 text-brand-muted">
      <Icon size={14} className="text-brand-primary" /> <span className="text-brand-ink">{label}</span>
    </span>
  );
  return href ? (
    <a href={href} className="hover:underline">
      {content}
    </a>
  ) : (
    content
  );
}

function fmtDateTime(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
